import React, { useMemo, useEffect } from 'react';
import { SimpleSelect } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';

export interface StreamlitAppSelectorProps {
  selectedStreamlitAppId: string | null;
  onSelectStreamlitApp: (appId: string) => void;
}

export const StreamlitAppSelector = ({
  selectedStreamlitAppId,
  onSelectStreamlitApp,
}: StreamlitAppSelectorProps) => {
  const { myObservation } = useObservation();
  const streamlitAppOptions = useMemo(() => {
    if (!myObservation) {
      return [];
    }

    return (
      myObservation.streamlitAppEntitlements
        // Only show apps that are not linked to a prototype custom viz, since those are shown in the filter bar for a particular viz instead.
        .filter((app) => app.prototypeCustomVizId === null)
        .map((app) => ({
          label: app.name,
          value: app.id,
        }))
    );
  }, [myObservation]);

  // Auto-select first app if none selected and apps are loaded
  useEffect(() => {
    if (streamlitAppOptions.length > 0 && !selectedStreamlitAppId) {
      onSelectStreamlitApp(streamlitAppOptions[0].value);
    }
  }, [streamlitAppOptions, selectedStreamlitAppId, onSelectStreamlitApp]);

  if (streamlitAppOptions.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded">
        <S2Typography
          variant="body-7"
          textColorVariant="gray-600"
          translatedContent="No Streamlit apps available"
        />
      </div>
    );
  }

  return (
    <div className="min-w-[200px]">
      <SimpleSelect
        selectedOptionValue={
          selectedStreamlitAppId || streamlitAppOptions[0]?.value || ''
        }
        options={streamlitAppOptions}
        updateOptions={onSelectStreamlitApp}
      />
    </div>
  );
};
